import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { Link } from 'react-router-dom';
import PageShell, { PageHero } from '../../v2/pages/PageShell';

const TermsOfUse: React.FC = () => {
  const { currentLanguage } = useTranslation();

  const fr = (
    <>
      <PageShell
        seo={{
          title: "Conditions générales d'utilisation | Lave Tes Baskets",
          description: "Les conditions d'accès et d'utilisation du site et des services Lave Tes Baskets.",
          canonicalPath: `/${currentLanguage}/terms`,
        }}
      >
        <PageHero
          crumbs={[{ label: "Accueil", to: `/${currentLanguage}` }, { label: "Conditions générales d'utilisation" }]}
          index="§"
          eyebrow={"Légal"}
          title={"Conditions générales d'utilisation"}
        />
        <section className="bg-bone py-14 text-ink sm:py-20">
          <div className="v2-prose mx-auto max-w-3xl px-4 sm:px-6">
              <p className="mb-4">Dernière mise à jour : 25/08/2025</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">1. Objet</h3>
              <p className="mb-4">Les présentes CGU encadrent l'accès et l'utilisation du site Lave Tes Baskets (LTB) et de ses services.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">2. Accès au service</h3>
              <p className="mb-4">Le site est accessible 7j/7, 24h/24, sous réserve des opérations de maintenance. LTB s'efforce d'assurer la continuité du service.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">3. Compte utilisateur</h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>Vous êtes responsable de la confidentialité de vos identifiants.</li>
                <li>Toute activité réalisée via votre compte est réputée faite par vous.</li>
                <li>LTB peut suspendre un compte en cas d'usage frauduleux.</li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">4. Commandes et prestations</h3>
              <p className="mb-4">Les prestations de nettoyage/réparation sont décrites sur le site. Les conditions spécifiques (prix, délais, retrait/livraison) sont précisées lors de la commande.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">5. Tarifs et paiements</h3>
              <p className="mb-4">Les prix sont indiqués en € TTC. Le paiement sécurisé est exigé selon les modalités proposées. Les factures sont disponibles sur demande.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">6. Responsabilité</h3>
              <p className="mb-4">LTB met en œuvre tous les moyens pour assurer la qualité du service. La responsabilité ne saurait être engagée pour un dommage indirect ou en cas de force majeure.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">7. Propriété intellectuelle</h3>
              <p className="mb-4">Les contenus et marques LTB sont protégés. Toute reproduction non autorisée est interdite.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">8. Données personnelles</h3>
              <p className="mb-4">Voir notre <Link to={`/${currentLanguage}/privacy`} className="text-accent-teal underline">Politique de Confidentialité</Link>.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">9. Droit applicable et litiges</h3>
              <p className="mb-4">Droit français. Tentative préalable de résolution amiable. À défaut, tribunaux compétents.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">10. Limitation et exclusions</h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>Résultats de nettoyage non garantis pour matières délicates (teintures instables, suède/nubuck, colles sensibles, pièces métalliques).</li>
                <li>Délais indicatifs non garantis; retards possibles en périodes de forte activité.</li>
                <li>LTB peut refuser toute paire présentant un risque élevé de détérioration.</li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">11. Responsabilités du client</h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>Fournir des informations exactes et un moyen de contact accessible.</li>
                <li>Retirer tout accessoire ou objet personnel des chaussures.</li>
                <li>Signaler les zones fragiles et les spécificités (matières, custom, couture).</li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">12. Perte, dommage et plafonds</h3>
              <p className="mb-4">En cas de perte ou dommage avéré imputable à LTB, l'indemnisation est limitée à un plafond raisonnable par paire, au choix de LTB: réparation, remplacement équivalent ou remboursement plafonné.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">13. Retrait/livraison manqués</h3>
              <p className="mb-4">Des frais peuvent s'appliquer en cas d'absence lors d'un retrait ou d'une livraison planifiés. Un nouveau créneau devra être reprogrammé.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">14. Articles non réclamés</h3>
              <p className="mb-4">Des frais de stockage peuvent s'appliquer au-delà d'une période de conservation raisonnable. Passé un certain délai, LTB pourra disposer de l'article conformément à la loi applicable.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">15. Annulation & remboursement</h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>Annulation gratuite avant préparation/prise en charge.</li>
                <li>Après prise en charge, les demandes sont traitées au cas par cas; les remboursements ne couvrent pas la simple insatisfaction subjective.</li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">16. Propriété intellectuelle</h3>
              <p className="mb-4">Le site, la marque, les textes, visuels et processus restent la propriété de LTB. Toute reproduction sans autorisation est interdite.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">17. Utilisation acceptable</h3>
              <p className="mb-4">Interdiction d'abuser du service, de perturber le site ou de harceler le support. LTB peut suspendre l'accès en cas d'abus.</p>
            </div>
        </section>
      </PageShell>
    </>
  );

  const en = (
    <>
      <PageShell
        seo={{
          title: "Terms of use | Lave Tes Baskets",
          description: "Conditions for accessing and using the Lave Tes Baskets website and services.",
          canonicalPath: `/${currentLanguage}/terms`,
        }}
      >
        <PageHero
          crumbs={[{ label: "Home", to: `/${currentLanguage}` }, { label: "Terms of use" }]}
          index="§"
          eyebrow={"Legal"}
          title={"Terms of use"}
        />
        <section className="bg-bone py-14 text-ink sm:py-20">
          <div className="v2-prose mx-auto max-w-3xl px-4 sm:px-6">
              <p className="mb-4">Last updated: 2025-08-25</p>
              <p className="mb-4">These Terms govern access to and use of Lave Tes Baskets (LTB) and its services. They are governed by French law. Where relevant, references are made to French legislation applicable to online services and consumer protection.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">1. Purpose</h3>
              <p className="mb-4">These Terms define the conditions under which you may access and use the LTB website and services.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">2. Service availability</h3>
              <p className="mb-4">The site is generally available 24/7 subject to maintenance operations. LTB strives to ensure service continuity without guaranteeing uninterrupted access.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">3. User account</h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>You are responsible for keeping your credentials confidential.</li>
                <li>Any activity conducted through your account is deemed to be carried out by you.</li>
                <li>LTB may suspend an account in case of fraudulent or abusive use.</li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">4. Orders and services</h3>
              <p className="mb-4">Cleaning/repair services are described on the website. Specific conditions (pricing, timelines, pickup/delivery) are provided at checkout in accordance with French consumer information requirements.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">5. Pricing and payments</h3>
              <p className="mb-4">Prices are indicated in EUR including VAT. Secure payment is required via the available methods. Invoices can be provided upon request.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">6. Liability</h3>
              <p className="mb-4">LTB implements reasonable measures to ensure service quality. LTB shall not be liable for indirect damages or in events of force majeure under French law.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">7. Intellectual property</h3>
              <p className="mb-4">LTB content and trademarks are protected. Any unauthorized reproduction is prohibited.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">8. Personal data</h3>
              <p className="mb-4">See our <Link to={`/${currentLanguage}/privacy`} className="text-accent-teal underline">Privacy Policy</Link>, which complies with the GDPR and French Data Protection Act and is supervised by the CNIL.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">9. Governing law and disputes</h3>
              <p className="mb-4">These Terms are governed by French law. Parties shall first attempt amicable resolution. Failing that, competent French courts shall have jurisdiction.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">10. Limitations and exclusions</h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>Cleaning outcomes are not guaranteed for delicate materials (unstable dyes, suede/nubuck, sensitive glues, metal parts).</li>
                <li>Timeframes are indicative; delays may occur during peak periods.</li>
                <li>LTB may refuse any pair that poses a high risk of deterioration.</li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">11. Customer responsibilities</h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>Provide accurate information and a reachable contact method.</li>
                <li>Remove any accessories or personal items from shoes.</li>
                <li>Disclose fragile areas and specifics (materials, custom work, stitching).</li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">12. Loss, damage and caps</h3>
              <p className="mb-4">If a proven loss or damage is attributable to LTB, compensation is limited to a reasonable per‑pair cap, at LTB’s discretion: repair, replacement with an equivalent item, or a capped refund.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">13. Missed pickup/delivery</h3>
              <p className="mb-4">Fees may apply in case of absence at a scheduled pickup or delivery. A new slot must be booked.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">14. Unclaimed items</h3>
              <p className="mb-4">Storage fees may apply beyond a reasonable retention period. After a certain deadline, LTB may dispose of the item in accordance with applicable law.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">15. Cancellation & refunds</h3>
              <ul className="list-disc ml-6 space-y-2">
                <li>Free cancellation before preparation/pickup.</li>
                <li>After pickup, requests are handled case‑by‑case; refunds do not cover purely subjective dissatisfaction.</li>
              </ul>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">16. Intellectual property</h3>
              <p className="mb-4">The website, brand, texts, visuals and processes remain LTB’s property. Any reproduction without authorization is prohibited.</p>

              <h3 className="text-xl sm:text-2xl font-semibold mt-8 mb-3">17. Acceptable use</h3>
              <p className="mb-4">Do not abuse the service, disrupt the website, or harass support. LTB may suspend access in case of abuse.</p>
            </div>
        </section>
      </PageShell>
    </>
  );

  return currentLanguage === 'fr' ? fr : en;
};

export default TermsOfUse;
